import { useEffect, useState } from 'react';
import type { Stats } from '@phidio/protocol';

const LINES = [
  'Looking for someone interesting…',
  'Scanning the planet…',
  'Someone out there is about to say hi.',
  'Warming up the introduction…',
  'Matching on your interests…',
];

interface Props {
  interests: string[];
  strict: boolean;
  stats: Stats;
  searchStartedAt: number | null;
}

export function SearchingOverlay({ interests, strict, stats, searchStartedAt }: Props) {
  const [line, setLine] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setLine((value) => (value + 1) % LINES.length), 2600);
    return () => window.clearInterval(id);
  }, []);

  const waited = searchStartedAt ? Math.floor((Date.now() - searchStartedAt) / 1000) : 0;

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 bg-ink-950/80 px-6 text-center backdrop-blur-md">
      <div className="relative flex h-32 w-32 items-center justify-center">
        <span className="absolute inset-0 rounded-full border border-phidio-500/30" />
        <span className="absolute inset-0 rounded-full bg-phidio-500/25 animate-pulse-ring" />
        <span className="absolute inset-0 rounded-full bg-phidio-500/25 animate-pulse-ring [animation-delay:-0.8s]" />
        <span className="h-14 w-14 animate-spin rounded-full border-[3px] border-white/10 border-t-phidio-500" />
      </div>

      <div>
        <p className="text-xl font-light">{LINES[line]}</p>
        <p className="mt-2 text-sm text-white/40 tabular-nums">
          {stats.waiting > 0
            ? `${stats.waiting.toLocaleString()} in the queue · ${stats.online.toLocaleString()} online`
            : `${stats.online.toLocaleString()} people online`}
          {waited > 5 ? ` · ${waited}s` : ''}
        </p>
      </div>

      {interests.length > 0 && (
        <ul className="flex flex-wrap justify-center gap-2">
          {interests.map((tag) => (
            <li
              key={tag}
              className="rounded-full bg-white/[0.06] px-3 py-1 text-xs text-white/50 ring-1 ring-white/10"
            >
              #{tag}
            </li>
          ))}
        </ul>
      )}

      {strict && interests.length > 0 && (
        <p className="max-w-xs text-xs text-white/30">
          Strict matching is on — you'll only meet people who share one of these.
        </p>
      )}
    </div>
  );
}
